// maths.portll.net — public landing.
//
// Marketing copy is fully public: no signup gate on anything a family reads before
// deciding. Two promises, side by side — home maths practice matched to what Australian
// kids actually learn, and a Grown-Ups' Corner of hands-on activities you do together
// away from the screen. The only call to action is a warm "Sign in". Closes with the
// ACARA-independence footer (we align to the curriculum; we are not affiliated with it).

import { Link } from "react-router-dom";
import { Button, Card, StrandBadge, injectStyles } from "../components/ui.jsx";

const STRANDS = ["number", "algebra", "measurement", "space", "statistics", "probability"];

const CORNER = [
  {
    icon: "🚗",
    title: "Real-world missions",
    body: "Count car colours on the footpath, weigh the shopping, time the walk to the park — everyday moments that turn into maths.",
  },
  {
    icon: "✏️",
    title: "Print & collect offline",
    body: "Print a tidy recording sheet, gather the data on paper together, then type the totals back in when you’re ready.",
  },
  {
    icon: "💬",
    title: "Talk it through",
    body: "Simple prompts help you ask the good questions — “what do you notice?”, “what would you guess?” — no marking, no pressure.",
  },
];

export default function Landing() {
  return (
    <div className="ld-page">
      <header className="ld-topbar">
        <Link to="/" className="ld-brand" aria-label="maths.portll.net home">
          <span aria-hidden="true">🌿</span>
          <span>maths<span className="ld-brand__dot">·</span>portll</span>
        </Link>
        <Button to="/login" variant="secondary" size="sm">
          Sign in
        </Button>
      </header>

      <main className="ld-main">
        <section className="ld-hero">
          <p className="ld-eyebrow">Made in Australia · Foundation to Year 6</p>
          <h1 className="ld-title">Home maths that matches what your child learns at school.</h1>
          <p className="ld-lead">
            Playful practice matched to the Australian Curriculum — plus a Grown-Ups’ Corner
            of hands-on activities you do together, away from the screen.
          </p>
          <div className="ld-cta">
            <Button to="/play" variant="primary" size="lg">
              Free Play (Beta)
            </Button>
            <a className="ld-cta__ghost" href="#corner">
              See the Grown-Ups’ Corner ↓
            </a>
          </div>
          <ul className="ld-strands" aria-label="Maths strands we cover">
            {STRANDS.map((s) => (
              <li key={s}>
                <StrandBadge strand={s} />
              </li>
            ))}
          </ul>
        </section>

        <section className="ld-two" aria-label="What’s inside">
          <Card className="ld-feature">
            <div className="ld-feature__icon" aria-hidden="true">🎮</div>
            <h2 className="ld-feature__title">For the kids</h2>
            <p className="ld-feature__body">
              Warm, playful maths matched to your child’s year level. Progress is something
              they <em>explore</em>, never a score or a grade — the goal is confidence, not marks.
            </p>
          </Card>
          <Card className="ld-feature">
            <div className="ld-feature__icon" aria-hidden="true">🫖</div>
            <h2 className="ld-feature__title">The Grown-Ups’ Corner</h2>
            <p className="ld-feature__body">
              A quiet space for you: hands-on activities to do together, printable recording
              sheets, and a simple way to see what your child has been exploring.
            </p>
          </Card>
        </section>

        <section className="ld-corner" id="corner" aria-label="Hands-on activities">
          <h2 className="ld-corner__title">Maths that happens at the kitchen table</h2>
          <p className="ld-corner__lead">
            The best maths often isn’t on a screen at all. The Grown-Ups’ Corner gives you
            small, joyful things to do together.
          </p>
          <div className="ld-cards">
            {CORNER.map((c) => (
              <Card key={c.title} className="ld-mini">
                <div className="ld-mini__icon" aria-hidden="true">{c.icon}</div>
                <h3 className="ld-mini__title">{c.title}</h3>
                <p className="ld-mini__body">{c.body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="ld-final">
          <Card className="ld-final__card">
            <h2 className="ld-final__title">Ready to explore together?</h2>
            <p className="ld-final__body">
              maths.portll.net is in a small invite-only beta. Sign in with your invite to
              get started.
            </p>
            <Button to="/login" variant="primary" size="lg">
              Sign in
            </Button>
          </Card>
        </section>
      </main>

      <footer className="ld-foot">
        <p className="ld-indep">
          maths.portll.net is an independent resource made for families. It is not
          affiliated with, endorsed by, or connected to ACARA or the Australian Government.
          “Australian Curriculum” is referenced only to describe the learning our activities
          align with; curriculum codes are used for that alignment and remain the property
          of their owners.
        </p>
        <p className="ld-foot__links">
          <Link to="/login">Sign in</Link>
          <span aria-hidden="true"> · </span>
          <span>© {new Date().getFullYear()} maths.portll.net</span>
        </p>
      </footer>
    </div>
  );
}

const LD_CSS = `
.ld-page{min-height:100%;display:flex;flex-direction:column;}
.ld-topbar{display:flex;align-items:center;justify-content:space-between;gap:1rem;
  padding:.9rem clamp(1rem,4vw,2rem);max-width:64rem;width:100%;margin:0 auto;}
.ld-brand{display:inline-flex;align-items:center;gap:.45rem;text-decoration:none;color:var(--brand-ink);
  font-weight:800;font-size:1.15rem;letter-spacing:-.01em;}
.ld-brand__dot{color:var(--accent);}
.ld-main{flex:1 1 auto;width:100%;max-width:64rem;margin:0 auto;padding:0 clamp(1rem,4vw,2rem);}

.ld-hero{text-align:center;padding:clamp(1.5rem,6vw,3.5rem) 0 1.5rem;}
.ld-eyebrow{margin:0 0 .6rem;font-weight:700;font-size:.82rem;letter-spacing:.04em;text-transform:uppercase;color:var(--brand);}
.ld-title{margin:0 auto;max-width:20ch;font-size:clamp(1.9rem,6vw,3rem);line-height:1.1;}
.ld-lead{margin:1rem auto 0;max-width:46ch;font-size:clamp(1.02rem,2.4vw,1.2rem);color:var(--ink-soft);}
.ld-cta{display:flex;flex-wrap:wrap;gap:.75rem 1.1rem;align-items:center;justify-content:center;margin:1.6rem 0 0;}
.ld-cta__ghost{color:var(--brand-ink);font-weight:700;text-decoration:none;min-height:44px;display:inline-flex;align-items:center;}
.ld-strands{display:flex;flex-wrap:wrap;gap:.5rem;justify-content:center;list-style:none;margin:1.8rem 0 0;padding:0;}

.ld-two{display:grid;grid-template-columns:repeat(auto-fit,minmax(15rem,1fr));gap:1rem;margin:1.5rem 0;}
.ld-feature{padding:1.4rem 1.4rem;}
.ld-feature__icon{font-size:1.9rem;line-height:1;}
.ld-feature__title{margin:.5rem 0 .35rem;font-size:1.25rem;}
.ld-feature__body{margin:0;color:var(--ink-soft);}

.ld-corner{margin:2.5rem 0 1rem;text-align:center;}
.ld-corner__title{margin:0;font-size:clamp(1.4rem,4vw,1.9rem);}
.ld-corner__lead{margin:.6rem auto 1.5rem;max-width:48ch;color:var(--ink-soft);}
.ld-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(14rem,1fr));gap:1rem;text-align:left;}
.ld-mini{padding:1.2rem 1.2rem;}
.ld-mini__icon{font-size:1.6rem;line-height:1;}
.ld-mini__title{margin:.5rem 0 .3rem;font-size:1.1rem;}
.ld-mini__body{margin:0;color:var(--ink-soft);font-size:.95rem;}

.ld-final{margin:2.5rem 0 1rem;}
.ld-final__card{text-align:center;padding:2rem 1.4rem;
  background:color-mix(in srgb,var(--brand) 7%,var(--card));border-color:color-mix(in srgb,var(--brand) 22%,var(--line));}
.ld-final__title{margin:0;font-size:clamp(1.4rem,4vw,1.8rem);}
.ld-final__body{margin:.6rem auto 1.2rem;max-width:42ch;color:var(--ink-soft);}

.ld-foot{margin-top:auto;border-top:1px solid var(--line);background:color-mix(in srgb,var(--ink-soft) 4%,var(--paper));}
.ld-indep{max-width:64rem;margin:0 auto;padding:1.5rem clamp(1rem,4vw,2rem) .5rem;
  font-size:.8rem;line-height:1.6;color:var(--ink-soft);}
.ld-foot__links{max-width:64rem;margin:0 auto;padding:0 clamp(1rem,4vw,2rem) 1.5rem;
  font-size:.85rem;color:var(--ink-soft);}
.ld-foot__links a{color:var(--brand);font-weight:600;text-decoration:none;}
`;

injectStyles("maths-landing-styles", LD_CSS);
